import { defineElement, getEasternDate } from '~/utils';
import { formatDate, getLongDate } from '~/utils/calendar-utils';
import { DeliveryDateService } from '~/services/delivery-date-2';

const ALTERNATE_DATE_STORAGE_KEY = 'alternate-date-selected';

/**
 * DeliveryDateForm is a class that handles the delivery date picker logic
 * @property {DeliveryDateService} deliveryDateService - the delivery date service
 * @method onRightAwayCheckboxClick - handles the right away checkbox click
 * @method onAlternateCheckboxClick - handles the alternate date checkbox click
 * @method setAlternateDate - sets the alternate date
 */
class DeliveryDateForm extends HTMLElement {
  /** @type {DeliveryDateService} */
  deliveryDateService;

  constructor() {
    super();

    const productBlockOutDates = this.getAttribute('product-block-out-dates');
    const productSoldOutDates = this.getAttribute('product-sold-out-dates');
    const productPreorderDatesAttr = this.getAttribute('product-preorder-dates');
    let productPreorderDates = null;
    if (productPreorderDatesAttr) {
      try {
        productPreorderDates = JSON.parse(productPreorderDatesAttr);
      } catch (e) { /* ignore */ }
    }
    this.boardType = this.hasAttribute('is-dessert-board') ? 'dessert-board' : 'cheese-board';
    this.deliveryDateService = new DeliveryDateService(window.Boarderie, productBlockOutDates, productSoldOutDates, productPreorderDates);
  }

  get isEgift() {
    return this.hasAttribute('is-egift');
  }

  get isEgiftRedemption() {
    return this.hasAttribute('egift-redemption');
  }

  get isGiftCard() {
    return this.hasAttribute('is-gift-card');
  }

  get orderEditMode() {
    return this.hasAttribute('order-edit-mode');
  }

  /**
   * Get the date picker
   * @returns {DeliveryDatePicker} date picker
   */
  get datePicker() {
    return this.querySelector('delivery-date-picker') || this.querySelector('date-picker');
  }

  get activeDeliveryMethod() {
    const $checkboxRightAway = this.querySelector('[data-delivery-date-checkbox-right-away]');
    const $checkboxAlternateDate = this.querySelector('[data-delivery-date-checkbox-alternate]');

    if ($checkboxRightAway?.checked) {
      return 'right-away';
    }

    if ($checkboxAlternateDate?.checked) {
      return 'alternate';
    }

    return 'right-away';
  }

  get currentDeliveryData() {
    return {
      deliveryDate: this.selectedDeliveryDate,
      deliveryDateISO: this.selectedDeliveryDateISO,
      shippingDate: this.selectedShippingDate,
      deliveryMethod: this.activeDeliveryMethod,
    };
  }

  get fees() {
    return this.deliveryDateService.getFees(this.selectedDeliveryDate, this.boardType);
  }

  connectedCallback() {
    if (this.datePicker?.ready) {
      this.addEventListener('date-picker::calendar-ready', this.#onPickerReady.bind(this));
      this.addEventListener('date-picker::date-change', this.#onDateChange.bind(this));
    } else {
      this.addEventListener('date-picker::ready', () => {
        this.addEventListener('date-picker::calendar-ready', this.#onPickerReady.bind(this));
        this.addEventListener('date-picker::date-change', this.#onDateChange.bind(this));
      });
    }

    // Right away checkbox
    const $checkboxRightAway = this.querySelector('[data-delivery-date-checkbox-right-away]');
    if ($checkboxRightAway) {
      $checkboxRightAway.addEventListener('click', this.#onRightAwayCheckboxClick.bind(this));
    }

    // Alternate date checkbox
    const $checkboxAlternateDate = this.querySelector('[data-delivery-date-checkbox-alternate]');
    if ($checkboxAlternateDate) {
      $checkboxAlternateDate.addEventListener('click', this.#onAlternateCheckboxClick.bind(this));
    }

    // Render the delivery date
    if (this.isEgift) {
      const { date } = getEasternDate();
      const formattedToday = getLongDate(date);
      this.querySelector('[data-next-delivery-date]').innerHTML = "- " + formattedToday;
    } else {
      const skipPremiumDay = this.boardType === 'dessert-board';
      const formattedDeliveryDate = this.deliveryDateService.getFormattedDeliveryDate('full-date', skipPremiumDay);
      const premiumDayShippingProfile = window.cartStrings.premiumDayShippingProfile

      if (skipPremiumDay) {
        const premiumDayShippingProfileText = premiumDayShippingProfile
          ? ` (${premiumDayShippingProfile})`
          : '';

        this.querySelector('[data-next-delivery-date]').innerHTML = formattedDeliveryDate + premiumDayShippingProfileText
      } else {
        this.querySelector('[data-next-delivery-date]').innerHTML = "- " + formattedDeliveryDate
      }
    }

    // Hide 'Right Away' text if it's a premium delivery day
    if (this.boardType === 'dessert-board' && this.deliveryDateService.isPremiumDeliveryDay(this.deliveryDateService.deliveryDate)) {
      this.querySelector('[data-right-away-text]').style.setProperty('display', 'none');
    }
  }

  #initDefaultValues() {
    if (this.activeDeliveryMethod === 'right-away') {
      this.#setNextDeliveryDate();
    } else if (this.activeDeliveryMethod === 'alternate' && this.datePicker.value) {
      this.#setAlternateDate(this.datePicker.value);
    } else {
      this.#setNextDeliveryDate();
    }
  }

  #onPickerReady() {
    this.#initDefaultValues();
  }

  #onRightAwayCheckboxClick() {
    this.#setNextDeliveryDate();
  }

  #onAlternateCheckboxClick() {
    this.#setAlternateDate(this.datePicker.value);
    this.datePicker.open();
  }

  #setAlternateDate(selectedDate) {
    // Update this.arrivalDateInput and this.shipByDateInput with the selected date
    // this.arrivalDateInput should get the selectedDate
    // this.shipByDateInput should get the selectedDate - 1 (overnight) or - 2 (standard for dessert boards)

    // selectedDate is in ISO format, so we need to convert it to a Date object

    // TODO: Make sure you are getting Eastern time zone date
    // flatpickr by default use javascript date object which is in local timezone
    // https://github.com/flatpickr/flatpickr/issues/930

    if (!selectedDate) return;
    this.selectedDeliveryDate = new Date(selectedDate);
    this.selectedDeliveryDateISO = selectedDate;
    this.selectedShippingDate = new Date(selectedDate);

    // For dessert boards, check if this is a premium (overnight) delivery day
    const isDessertBoard = this.boardType === 'dessert-board';
    const isOvernightDelivery = isDessertBoard && this.deliveryDateService.isPremiumDeliveryDay(this.selectedDeliveryDate);

    if (isDessertBoard) {
      // Premium days (Tuesday + any merchant-configured days) ship 1 day before;
      // standard dessert deliveries ship 2 days before.
      const daysBeforeDelivery = isOvernightDelivery ? 1 : 2;
      this.selectedShippingDate.setDate(this.selectedShippingDate.getDate() - daysBeforeDelivery);
      this.isOvernightDelivery = isOvernightDelivery;
    } else {
      // Other boards: always ship 1 day before
      this.selectedShippingDate.setDate(this.selectedShippingDate.getDate() - 1);
    }

    this.f_selectedDeliveryDate = formatDate({ date: this.selectedDeliveryDate, separator: '/' });

    // If EGIFT make both shipping dates the same.
    if (this.isEgift) {
      this.selectedShippingDate = this.selectedDeliveryDate;
    }

    this.f_selectedShippingDate = formatDate({ date: this.selectedShippingDate, separator: '/' });

    // Notify other components (like delivery-urgency-bar) that an alternate date was selected
    sessionStorage.setItem(ALTERNATE_DATE_STORAGE_KEY, 'true');
    document.dispatchEvent(new CustomEvent('alternate-date-selected', {
      detail: { date: selectedDate }
    }));
  }

  #setNextDeliveryDate() {
    if (this.isEgift) {
      const { date } = getEasternDate();
      this.selectedDeliveryDate = date;
      this.selectedDeliveryDateISO = date.toISOString();
      this.selectedShippingDate = date;
    } else if (this.boardType === 'dessert-board') {
      // For dessert boards, use non-premium dates by default (standard 2-day shipping)
      this.selectedDeliveryDate = this.deliveryDateService.nonPremiumDeliveryDate;
      this.selectedDeliveryDateISO = this.deliveryDateService.nonPremiumDeliveryDateISO;
      this.selectedShippingDate = this.deliveryDateService.nonPremiumShippingDate;
      this.isOvernightDelivery = false;
    } else {
      // For cheese boards, use standard delivery dates (1-day shipping)
      this.selectedDeliveryDate = this.deliveryDateService.deliveryDate;
      this.selectedDeliveryDateISO = this.deliveryDateService.deliveryDateISO;
      this.selectedShippingDate = this.deliveryDateService.shippingDate;
    }

    this.f_selectedDeliveryDate = formatDate({ date: this.selectedDeliveryDate, separator: '/' });
    this.f_selectedShippingDate = formatDate({ date: this.selectedShippingDate, separator: '/' });

    // Notify other components (like delivery-urgency-bar) that the alternate date was cleared
    sessionStorage.removeItem(ALTERNATE_DATE_STORAGE_KEY);
    document.dispatchEvent(new CustomEvent('alternate-date-cleared'));
  }

  #onDateChange(event) {
    const $checkboxAlternateDate = this.querySelector('[data-delivery-date-checkbox-alternate]');
    if ($checkboxAlternateDate && !$checkboxAlternateDate.checked) {
      $checkboxAlternateDate.checked = true;
      $checkboxAlternateDate.dispatchEvent(new Event('change'));
    }

    this.#setAlternateDate(event.detail.date);
  }
}

if (!customElements.get('delivery-date-form')) {
  defineElement('delivery-date-form', DeliveryDateForm);
}
